import successCheck from 'assets/lottie/successCheck.json';
import { Button, ButtonStyle } from 'common/button';
import { Breakpoints } from 'common/enums';
import { classnames, getQueryParam } from 'helpers/utils';
import { useMediaQuery } from 'hooks/use-media-query';
import Lottie from 'lottie-react';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { VaultCard } from 'pages/sign-up-onboarding/components/vault-card/vault-card';
import { useEffect, useState } from 'react';
import { goToPage, RouteName } from 'routes';
import styles from './vault-subject-selection.module.scss';

const OnboardingSubjectSelection: React.FC = () => {
  const isMobile = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);
  const vaultId = getQueryParam('vaultId') ?? '';
  const [vaultTitle, setVaultTitle] = useState<string>('');
  const [vaultIcon, setVaultIcon] = useState<string | undefined>('');
  const [selectedOption, setSelectedOption] = useState('');

  const [showConfirmRole, setShowConfirmeRole] = useState(false);

  const fetchVault = async () => {
    const response = await VaultsController.getVault(Number(vaultId));
    setVaultTitle(response.title);
    setVaultIcon(response.coverUrl);
  };

  useEffect(() => {
    fetchVault();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  const handleClick = () => {
    if (!selectedOption) return;

    if (!showConfirmRole) {
      setShowConfirmeRole(true);
    } else {
      goToPage(RouteName.OnboardingVaultInfo, {}, { selectedOption, vaultId });
    }
  };

  const isContinueDisabled = !selectedOption && !showConfirmRole;

  const showOnboardingVaultConfirmRole = () => (
    <div className={styles.content}>
      <div>
        <Lottie
          animationData={successCheck}
          className={styles.lottie}
          loop={false}
        />
        {selectedOption === 'aboutMe' ? (
          <span className="text__heading4__gray800">
            You&apos;re now an editor of your vault!
          </span>
        ) : (
          <span className="text__heading4__gray800">
            You&apos;re now an editor for {vaultTitle}!
          </span>
        )}
      </div>
      {selectedOption === 'aboutMe' ? (
        <span className="text__body__regular__large__gray700">
          Everything you add will help turn your life into a lasting legacy.
        </span>
      ) : (
        <span className="text__body__regular__large__gray700">
          Your contributions will help turn {vaultTitle} life into a lastings
          legacy.
        </span>
      )}
    </div>
  );

  const showOnboardingSubjectSelection = () => (
    <div className={styles.content}>
      <span className="text__heading4__gray800">Is this vault about you?</span>
      <div className={styles.containerRadioButtons}>
        <label
          className={classnames(
            selectedOption === 'aboutMe'
              ? styles.radioButtonSelected
              : styles.radioButtonsLabel,
            'text__body__regular__large__gray700',
          )}
          htmlFor="aboutMe"
        >
          <input
            type="radio"
            value="aboutMe"
            checked={selectedOption === 'aboutMe'}
            onChange={handleChange}
            className={styles.radioButton}
            id="aboutMe"
          />
          <strong>Yes,</strong> it&apos;s about me
        </label>
        <label
          className={classnames(
            selectedOption === 'aboutSomeoneElse'
              ? styles.radioButtonSelected
              : styles.radioButtonsLabel,
            'text__body__regular__large__gray700',
          )}
          htmlFor="aboutSomeoneElse"
        >
          <input
            type="radio"
            value="aboutSomeoneElse"
            checked={selectedOption === 'aboutSomeoneElse'}
            onChange={handleChange}
            className={styles.radioButton}
            id="aboutSomeoneElse"
          />
          <strong>No,</strong> it&apos;s for someone else
        </label>
      </div>
    </div>
  );

  return (
    <div className={styles.containerVault}>
      {!isMobile && (
        <VaultCard
          image={vaultIcon}
          name={vaultTitle}
          subtitle="A life story"
        />
      )}
      <div className={styles.containerRight}>
        {showConfirmRole
          ? showOnboardingVaultConfirmRole()
          : showOnboardingSubjectSelection()}
        <Button
          buttonStyle={ButtonStyle.PrimaryFilled}
          className={styles.button}
          onClick={handleClick}
          disabled={isContinueDisabled}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export { OnboardingSubjectSelection };
